import { useEffect } from 'react';
import Navigation from './Navigation';
import { useNavigate, useLocation } from 'react-router';
import { CircularProgress, styled } from '@mui/material';
import { UserRole } from '@/lib/types';
import { useAuthContext } from '@/contexts/AuthContext';
import { storePostLoginRedirect } from '@/lib/postLoginRedirect';

const SecurePage = ({
  requireLogin = false,
  requiredPermissions = [],
  children,
  allowedRoles,
  showNavigation = true,
}: {
  requireLogin?: boolean;
  requiredPermissions?: string[];
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  showNavigation?: boolean;
}) => {
  const { user, isPending } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isUnauthorized =
    !!user &&
    !isPending &&
    ((allowedRoles?.length &&
      !user?.roles.some((r) => allowedRoles.includes(r.name))) ||
      (requiredPermissions?.length &&
        !requiredPermissions.every((p) =>
          user?.permissions?.some((perm) => perm.key === p),
        )));
  const isLoggedOut =
    (requireLogin ||
      allowedRoles?.length ||
      requiredPermissions?.length) &&
    !user &&
    !isPending;
  const showLoader = isPending || isUnauthorized || isLoggedOut;

  useEffect(() => {
    if (isLoggedOut) {
      // Remember where they were heading; AuthContext sends them back once
      // they have signed in. Auth pages are rejected by the helper, so the
      // re-run this navigation causes cannot overwrite the real target.
      storePostLoginRedirect(
        location.pathname + location.search + location.hash,
      );
      navigate('/login', { replace: true });
    }
  }, [isLoggedOut, location, navigate]);

  useEffect(() => {
    if (isUnauthorized) {
      navigate('/unauthorized', { replace: true });
    }
  }, [isUnauthorized, navigate]);

  return (
    <>
      {showNavigation && <Navigation />}
      {showLoader ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        children
      )}
    </>
  );
};

const LoaderWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
});

const Loader = styled(CircularProgress)({
  margin: 'auto',
});

export default SecurePage;
